import { useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import { Footer, Header, Nav, NewFeature, SignUpDialog } from '@/components';
import { useGetCollections, useGetProfile } from '@/hooks';
import { useBearStore } from '@/store';

const App = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [searchParams] = useSearchParams();
  const blueprintId = searchParams.get('blueprint_id');

  const { session, setCollections } = useBearStore();
  const setColumns = useBearStore(state => state.setColumns);
  const showSignUpDialog = useBearStore(state => state.showSignUpDialog);
  const setShowSignUpDialog = useBearStore(state => state.setShowSignUpDialog);
  const getCollections = useGetCollections();
  const getProfile = useGetProfile();

  useEffect(() => {
    if (getCollections.data) {
      setCollections(getCollections.data);
    }
  }, [getCollections.data]);

  const updateColumns = useCallback(() => {
    const width = window.innerWidth;
    if (width < 600) setColumns(1);
    else if (width < 800) setColumns(2);
    else if (width < 1200) setColumns(3);
    else if (width < 1400) setColumns(4);
    else if (width < 1900) setColumns(5);
    else setColumns(6);
  }, [setColumns]);

  useEffect(() => {
    updateColumns();
    window.addEventListener('resize', updateColumns);
    return () => window.removeEventListener('resize', updateColumns);
  }, []);

  useEffect(() => {
    if (
      session &&
      getProfile.isError &&
      !getProfile.isLoading &&
      !getProfile.data
    ) {
      navigate(
        blueprintId
          ? `/new-profile?blueprint_id=${blueprintId}`
          : '/new-profile'
      );
    }
  }, [session, getProfile.isError, navigate]);

  return (
    <div className="mx-auto max-w-[2180px] px-4 pb-20 md:px-8">
      {/* self-referencing until a page (e.g. BlueprintDialog) sets a more specific one,
          so google never sees a blueprint page pointing its canonical at the home page */}
      <Helmet>
        <link rel="canonical" href={`https://fprints.xyz${pathname}`} />
        <meta property="og:url" content={`https://fprints.xyz${pathname}`} />
      </Helmet>
      <Header />
      <Nav />
      <NewFeature />
      <SignUpDialog
        open={showSignUpDialog}
        onOpenChange={setShowSignUpDialog}
      />
      <Outlet />
      <Footer />
    </div>
  );
};

export default App;
